// Dev-only freecam for the capture harness (tools/capture): URL params
// place the render camera at an absolute world pose, detached from the
// avatar, so a headless browser can screenshot named vistas. game.ts only
// parses this behind import.meta.env.DEV — production ignores the params.
//
//   ?cam=x,z,yaw,pitch[,y]   camera pose (world metres / radians)
//   &dy=1.7                  height above the ground when y is absent
//   &room=surface            area to join directly (must be a real plan id)
//   &scene=space             render THIS plan's scene instead of the joined
//                            room's. Capture-only: the Black admits no
//                            session without a ship (SpaceRoom hands a
//                            shipless join straight back to the pad), so
//                            the space vista joins a surface room and
//                            renders the space set. The set is the same
//                            createSpaceScene the real room uses; only the
//                            replicated entities in it are elsewhere.
//
// Conventions match the player pose: yaw 0 faces +z, π/2 faces +x (east
// along the settlement street), π faces −z (the club row). Pitch is
// radians above the horizon, so negative looks down.

import { isAreaId } from "@bebop/shared";
import * as THREE from "three";

export interface FreecamSpec {
  /** Area to join directly (default: wherever the server last saw us). */
  room?: string;
  /** Plan whose SCENE to render, when it differs from the joined room. */
  scene?: string;
  x: number;
  z: number;
  yaw: number;
  pitch: number;
  /** Absolute camera height; wins over dy when present. */
  y?: number;
  /** Metres above the ground under the camera when y is absent. */
  dy: number;
}

/** Read a freecam spec off a query string; undefined = not in capture mode. */
export function parseFreecam(search: string): FreecamSpec | undefined {
  const params = new URLSearchParams(search);
  const cam = params.get("cam");
  if (!cam) return undefined;
  const nums = cam.split(",").map(Number);
  if (nums.length < 4 || nums.some((n) => !Number.isFinite(n))) return undefined;
  const [x, z, yaw, pitch] = nums as [number, number, number, number];
  const y = nums[4];
  const room = params.get("room") ?? undefined;
  if (room !== undefined && !isAreaId(room)) return undefined;
  const scene = params.get("scene") ?? undefined;
  if (scene !== undefined && !isAreaId(scene)) return undefined;
  const dyRaw = Number(params.get("dy") ?? Number.NaN);
  const dy = Number.isFinite(dyRaw) ? dyRaw : 1.7;
  return { room, scene, x, z, yaw, pitch, y, dy };
}

const target = new THREE.Vector3();

/** Park the camera at the spec's pose, in render space (world − origin). */
export function applyFreecam(
  camera: THREE.PerspectiveCamera,
  spec: FreecamSpec,
  origin: { x: number; z: number },
  groundY: (x: number, z: number) => number,
): void {
  const y = spec.y ?? groundY(spec.x, spec.z) + spec.dy;
  const rx = spec.x - origin.x;
  const rz = spec.z - origin.z;
  camera.position.set(rx, y, rz);
  const cosPitch = Math.cos(spec.pitch);
  target.set(
    rx + Math.sin(spec.yaw) * cosPitch,
    y + Math.sin(spec.pitch),
    rz + Math.cos(spec.yaw) * cosPitch,
  );
  camera.lookAt(target);
}
